import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { useTranslations } from 'hooks/useTranslations';
import { renderLicenceDataColor } from 'components/molecules/Table/helpers';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate, leftDays } from 'utils/date';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { StyledLicenceInfo } from 'pages/LicencesPage/styles';
import { ELicenceStatusIds } from '../helpers';

interface ILicenceInfo {
  data: ITableDataSource;
  leftDaysNumber: number;
  t: any;
  leftDaysNotify: number;
  isActive: boolean;
}
const LicenceInfo = ({ data, leftDaysNumber, t, leftDaysNotify, isActive }: ILicenceInfo) => {
  const textToTooltip = [];
  if (data.hasInactiveOwner) {
    textToTooltip.push(t('legalEntity.licenceDetails.hasInactiveOwner.text'));
  }
  if (isActive) {
    if (data.hasAnyFirearmIllegal) {
      textToTooltip.push(t('legalEntity.licenceDetails.hasAnyIllegalFirearm.text'));
    }
    if (data.hasAnyFirearmInactive) {
      textToTooltip.push(t('legalEntity.licenceDetails.hasAnyInactiveFirearm.text'));
    }
    if (data.hasAnyLeDataChanged) {
      textToTooltip.push(t('legalEntity.licenceDetails.hasAnyLeDataChanged.text'));
    }
    if (data.hasAnyFirearmNotAssignedToLegal) {
      textToTooltip.push(t('legalEntity.licenceDetails.hasAnyFirearmNotAssignedToLegal.text'));
    }
    if (
      data.expirationDate &&
      leftDaysNumber !== null &&
      leftDaysNumber <= leftDaysNotify &&
      !data.renewed
    ) {
      textToTooltip.push(
        leftDaysNumber > 0
          ? `${t('legalEntity.licenceDetails.willExpire.text')} ${leftDaysNumber} ${t(
              'legalEntity.licenceDetails.willExpire.days',
            )}`
          : t('legalEntity.licenceDetails.willExpireToday.text'),
      );
    }
  }
  if (textToTooltip.length === 1) {
    return <span style={{ marginLeft: '10px' }}>{textToTooltip[0]}</span>;
  }

  return (
    <>
      {textToTooltip.map((label) => (
        <div>
          <li style={{ marginLeft: '10px' }}>{label}</li>
        </div>
      ))}
    </>
  );
};

interface ILegalEntityLicenceTableCard {
  data: ITableDataSource;
  showActions?: boolean;
  handleView: (arg: string) => void;
  handleRenew: (arg: string) => void;
  handleAmend: (arg: string) => void;
  leftDaysNotify: number;
  licenceAuthorityValid: boolean;
}

const LegalEntityLicenceTableCard = ({
  data,
  showActions = true,
  handleView,
  handleAmend,
  handleRenew,
  leftDaysNotify,
  licenceAuthorityValid,
}: ILegalEntityLicenceTableCard) => {
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const { shortDateFormat } = useGlobalProperty();

  const renderDate = (date: string) => formatDate(date, shortDateFormat);

  const isActive = data.statusUniqueId === ELicenceStatusIds.ACTIVE;
  const leftDaysNumber = Number(leftDays(data.expiryDate, t, true));
  const showInfo =
    data.hasInactiveOwner ||
    (isActive &&
      (data.hasAnyFirearmIllegal ||
        data.hasAnyFirearmInactive ||
        data.hasAnyLeDataChanged ||
        data.hasAnyFirearmNotAssignedToLegal ||
        (data.expiryDate && leftDaysNumber !== null && leftDaysNumber <= leftDaysNotify)));

  return (
    <Grid
      container
      sx={{
        backgroundColor: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        padding: '20px',
        rowGap: 3,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.weaponLicenceName.column')}
        </Typography>
        <Typography variant="body2">{data.weaponLicenceName}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.weaponLicenceNumber.column')}
        </Typography>
        <Typography variant="body2">{data.weaponLicenceNumber}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.createdDate.column')}
        </Typography>
        <Typography variant="body2">{renderDate(data.createdDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.startDate.column')}
        </Typography>
        <Typography variant="body2">{renderDate(data.startDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.expiryDate.column')}
        </Typography>
        <Typography variant="body2">{renderDate(data.expiryDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.licenceTab.status.column')}
        </Typography>
        <Typography variant="body2">
          <Chips text={data.status} variant={renderLicenceDataColor(data)?.stateField} />
        </Typography>
      </Grid>
      {showInfo && (
        <Grid size={{ xs: 12 }}>
          <Grid sx={{ alignItems: 'flex-start', gap: 1, display: 'flex' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('licences.info.label')}
            </Typography>
            <Icon type={EIconTypes.infoWarning} fontSize={20} />
          </Grid>
          <StyledLicenceInfo size={{ xs: 12 }}>
            <LicenceInfo
              data={data}
              leftDaysNumber={leftDaysNumber}
              leftDaysNotify={leftDaysNotify}
              t={t}
              isActive={isActive}
            />
          </StyledLicenceInfo>
        </Grid>
      )}
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          borderTop: `1px solid ${colors.primary100}`,
          pt: 2,
        }}
        spacing={1}
      >
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button
            id={`legal_entity_details_licence-view-${data.id}`}
            variant={EButtonVariants.outlined}
            label={t('legalEntityDetails.licenceTab.view.label')}
            onClick={() => handleView(data.id as string)}
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
            ]}
            permissionOr
            size={EButtonSizes.small}
            fullWidth
          />
        </Grid>
        {licenceAuthorityValid && (
          <>
            {data.statusUniqueId === ELicenceStatusIds.ACTIVE && data.canBeVaried && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  id={`legal_entity_details_licence-amend-${data.id}`}
                  variant={EButtonVariants.outlined}
                  label={t('legalEntityDetails.licenceTab.vary.label')}
                  onClick={() => handleAmend(data.id as string)}
                  permission={[EPermission.MANAGE_LE]}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
            {data.statusUniqueId === ELicenceStatusIds.ACTIVE && data.canBeRenewed && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  id={`legal_entity_details_licence-renew-${data.id}`}
                  variant={EButtonVariants.outlined}
                  label={t('legalEntityDetails.licenceTab.renew.label')}
                  onClick={() => handleRenew(data.id as string)}
                  permission={[EPermission.MANAGE_LE]}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export { LegalEntityLicenceTableCard };
